import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Save, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICE_FIELDS = [
    { key: 'essentiel_monthly_eur', label: 'Essentiel — mensuel (€ / mois)' },
    { key: 'essentiel_yearly_eur', label: 'Essentiel — annuel (€ / an)' },
    { key: 'pro_monthly_eur', label: 'Impact — mensuel (€ / mois)' },
    { key: 'pro_yearly_eur', label: 'Impact — annuel (€ / an)' },
    { key: 'organisation_monthly_eur', label: 'Organisation — mensuel (€ / mois)' },
    { key: 'organisation_yearly_eur', label: 'Organisation — annuel (€ / an)' },
    { key: 'trial_days', label: 'Essai gratuit (jours)' },
];

export default function PricingAdmin({ auth }) {
    const [pricing, setPricing] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [savingPrices, setSavingPrices] = useState(false);
    const [savingTier, setSavingTier] = useState(null);
    const [live, setLive] = useState(false);

    const reload = async () => {
        try {
            const r = await axios.get(`${API}/billing/stripe/catalog`, { headers: auth.headers });
            setPricing(r.data.pricing || {});
            setCatalog(r.data.plans || []);
            const pub = await axios.get(`${API}/billing/stripe/plans`);
            setLive(!!pub.data.live);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const savePrices = async () => {
        setSavingPrices(true);
        try {
            const body = {};
            PRICE_FIELDS.forEach(f => { body[f.key] = Number(pricing[f.key]); });
            await axios.put(`${API}/billing/stripe/pricing`, body, { headers: auth.headers });
            toast.success('Tarifs mis à jour — appliqués immédiatement aux nouveaux abonnements et à la page Tarifs');
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setSavingPrices(false);
    };

    const setTierField = (tier, field, value) => {
        setCatalog(prev => prev.map(p => p.tier === tier ? { ...p, [field]: value } : p));
    };

    const saveTier = async (p) => {
        setSavingTier(p.tier);
        try {
            await axios.put(`${API}/billing/stripe/catalog`, {
                [p.tier]: {
                    name: p.name, emoji: p.emoji, tagline: p.tagline, description: p.description,
                    cta: p.cta, cta_to: p.cta_to,
                    features: Array.isArray(p.features) ? p.features : String(p.features || '').split('\n').map(s => s.trim()).filter(Boolean),
                    order: Number(p.order) || 99,
                    visible: !!p.visible, recommended: !!p.recommended,
                },
            }, { headers: auth.headers });
            toast.success(`Offre « ${p.name} » mise à jour — visible immédiatement sur les pages publiques`);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setSavingTier(null);
    };

    if (!pricing) return <div className="flex justify-center py-16"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="space-y-8" data-testid="pricing-admin">
            <div className={`text-xs px-3 py-2 rounded-md border inline-block ${live ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                {live ? '✓ Compte Stripe LIVE connecté — paiements réels' : '⚠ Mode test — clé live non configurée'}
            </div>

            {/* Prix */}
            <div className="card-soft p-6 max-w-3xl">
                <h3 className="font-heading text-lg mb-4">💶 Prix des offres payantes</h3>
                <div className="grid sm:grid-cols-2 gap-5">
                    {PRICE_FIELDS.map(f => (
                        <div key={f.key} className="space-y-1.5">
                            <label className="text-sm font-medium">{f.label}</label>
                            <Input type="number" min={f.key === 'trial_days' ? 0 : 1}
                                value={pricing[f.key] ?? ''}
                                onChange={(e) => setPricing(v => ({ ...v, [f.key]: e.target.value }))}
                                data-testid={`pricing-input-${f.key}`} />
                        </div>
                    ))}
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                    Les nouveaux prix créent automatiquement les tarifs correspondants dans Stripe.
                    Les abonnements déjà en cours conservent leur prix d'origine.
                </p>
                <Button onClick={savePrices} disabled={savingPrices} className="mt-5 bg-primary hover:bg-primary-hover" data-testid="pricing-save-btn">
                    {savingPrices ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                    Enregistrer les tarifs
                </Button>
            </div>

            {/* Catalogue des offres */}
            <div>
                <h3 className="font-heading text-lg mb-1">🗂️ Catalogue des 5 offres publiques</h3>
                <p className="text-xs text-muted-foreground mb-4">
                    Nom, description, fonctionnalités, badge « Recommandé », ordre d'affichage, visibilité et CTA de chaque offre —
                    appliqués immédiatement sur la Landing et la page Tarifs.
                </p>
                <div className="space-y-4">
                    {catalog.map(p => (
                        <div key={p.tier} className="card-soft p-5" data-testid={`catalog-tier-${p.tier}`}>
                            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                                <div className="font-heading text-base flex items-center gap-2">
                                    <span>{p.emoji}</span> {p.name}
                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-surface-muted rounded px-1.5 py-0.5">tier : {p.tier}</span>
                                    {p.recommended && <Star size={13} className="text-primary fill-primary" />}
                                </div>
                                <div className="flex items-center gap-5 text-xs">
                                    <label className="flex items-center gap-2">
                                        <Switch checked={!!p.visible} onCheckedChange={(v) => setTierField(p.tier, 'visible', v)}
                                            data-testid={`catalog-visible-${p.tier}`} />
                                        Visible publiquement
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <Switch checked={!!p.recommended} onCheckedChange={(v) => setTierField(p.tier, 'recommended', v)}
                                            data-testid={`catalog-recommended-${p.tier}`} />
                                        Badge « Recommandé »
                                    </label>
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-medium">Nom commercial</label>
                                    <Input value={p.name || ''} onChange={(e) => setTierField(p.tier, 'name', e.target.value)}
                                        data-testid={`catalog-name-${p.tier}`} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium">Emoji</label>
                                    <Input value={p.emoji || ''} onChange={(e) => setTierField(p.tier, 'emoji', e.target.value)} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium">Ordre d'affichage</label>
                                    <Input type="number" value={p.order ?? ''} onChange={(e) => setTierField(p.tier, 'order', e.target.value)}
                                        data-testid={`catalog-order-${p.tier}`} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium">Libellé CTA</label>
                                    <Input value={p.cta || ''} onChange={(e) => setTierField(p.tier, 'cta', e.target.value)}
                                        data-testid={`catalog-cta-${p.tier}`} />
                                </div>
                                <div className="space-y-1 sm:col-span-2">
                                    <label className="text-xs font-medium">Cible (tagline)</label>
                                    <Input value={p.tagline || ''} onChange={(e) => setTierField(p.tier, 'tagline', e.target.value)} />
                                </div>
                                <div className="space-y-1 sm:col-span-2">
                                    <label className="text-xs font-medium">Lien CTA</label>
                                    <Input value={p.cta_to || ''} onChange={(e) => setTierField(p.tier, 'cta_to', e.target.value)} />
                                </div>
                                <div className="space-y-1 sm:col-span-2">
                                    <label className="text-xs font-medium">Description</label>
                                    <Textarea rows={2} value={p.description || ''} onChange={(e) => setTierField(p.tier, 'description', e.target.value)}
                                        data-testid={`catalog-description-${p.tier}`} />
                                </div>
                                <div className="space-y-1 sm:col-span-2">
                                    <label className="text-xs font-medium">Fonctionnalités (une par ligne)</label>
                                    <Textarea rows={4}
                                        value={Array.isArray(p.features) ? p.features.join('\n') : (p.features || '')}
                                        onChange={(e) => setTierField(p.tier, 'features', e.target.value)}
                                        data-testid={`catalog-features-${p.tier}`} />
                                </div>
                            </div>
                            <Button size="sm" onClick={() => saveTier(p)} disabled={savingTier === p.tier}
                                className="mt-4 bg-primary hover:bg-primary-hover" data-testid={`catalog-save-${p.tier}`}>
                                {savingTier === p.tier ? <Loader2 size={13} className="animate-spin mr-2" /> : <Save size={13} className="mr-2" />}
                                Enregistrer cette offre
                            </Button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
